import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const GlobalSEO = () => {
  const location = useLocation();
  const siteUrl = process.env.REACT_APP_CANONICAL_URL || 'https://safarnamah-by-saket-and-pallavi-lkvdoxo8t.vercel.app';
  const siteName = 'Safarnamah by Saket & Pallavi';
  const siteDescription = 'Discover Asia with expert travel guides by Saket Bundele and Pallavi Korade. Get authentic travel stories, detailed itineraries, and insider tips for Thailand, Vietnam, Dubai, Sri Lanka, Bali, Cambodia, Malaysia, Singapore, and Laos. Plan your perfect Asian adventure today.';
  const keywords = 'Safarnamah, Saket, saketbundele, safarnama, saket bundele safarnama, saket bundele, Saket bundele safarnamah, safrnam, pallavi korade, asia travel blog, southeast asia travel guide, thailand travel tips, vietnam travel itinerary, bali travel guide, dubai travel tips, sri lanka travel planning, cambodia angkor wat, malaysia travel guide, singapore travel, laos travel, saket bundele travel, pallavi korade travel, asian travel stories, backpacking asia, asia trip planning, cultural tourism asia, adventure travel asia';
  
  // Generate canonical URL with validation
  const generateCanonicalUrl = () => {
    const cleanPath = location.pathname.replace(/\/+/g, '/').replace(/\/$/, '') || '';
    const canonicalUrl = `${siteUrl}${cleanPath}`;
    
    try {
      new URL(canonicalUrl);
      return canonicalUrl;
    } catch (error) {
      console.warn(`Invalid canonical URL: ${canonicalUrl}, using base URL`);
      return siteUrl;
    }
  };
  
  const canonicalUrl = generateCanonicalUrl();

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "TravelAgency",
    "@id": `${siteUrl}/#organization`,
    "name": "Safarnamah",
    "alternateName": ["Safarnamah Travel Blog", "Safarnamah by Saket and Pallavi"],
    "url": siteUrl,
    "logo": {
      "@type": "ImageObject",
      "url": `${siteUrl}/logo.png`,
      "width": 250,
      "height": 60
    },
    "description": siteDescription,
    "slogan": "Your Gateway to Authentic Asian Adventures",
    "foundingDate": "2024",
    "areaServed": [
      "Thailand", "Vietnam", "Cambodia", "Indonesia", "Malaysia", 
      "Singapore", "Laos", "Sri Lanka", "United Arab Emirates", "Philippines"
    ],
    "serviceType": "Travel Planning and Consultation",
    "founders": [
      {
        "@type": "Person",
        "name": "Saket Bundele",
        "jobTitle": "Travel Blogger & Founder",
        "url": `${siteUrl}/about`,
        "sameAs": ["https://instagram.com/saketbundele"]
      },
      {
        "@type": "Person",
        "name": "Pallavi Korade",
        "jobTitle": "Travel Blogger & Founder", 
        "url": `${siteUrl}/about`,
        "sameAs": ["https://instagram.com/pallavikorade"]
      }
    ],
    "sameAs": [
      "https://www.instagram.com/safarnamah",
      "https://www.facebook.com/safarnamah",
      "https://twitter.com/safarnamah"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Service",
      "url": `${siteUrl}/contact`
    }
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": siteName,
    "url": siteUrl,
    "description": siteDescription,
    "inLanguage": "en-US",
    "copyrightYear": "2024",
    "copyrightHolder": {
      "@id": `${siteUrl}/#organization`
    },
    "publisher": {
      "@id": `${siteUrl}/#organization`
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${siteUrl}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    },
    "mainEntity": {
      "@type": "Blog",
      "@id": `${siteUrl}/#blog`,
      "name": "Safarnamah Travel Blog",
      "description": "Expert travel guides and stories from Asia",
      "blogPost": [],
      "author": {
        "@id": `${siteUrl}/#organization`
      }
    }
  };

  return (
    <Helmet>
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Primary Meta Tags */}
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Saket Bundele, Pallavi Korade" />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="3 days" />
      <meta name="rating" content="General" />
      <meta name="distribution" content="global" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content="@safarnamah" />
      <meta name="twitter:site" content="@safarnamah" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          "@id": `${siteUrl}/#blog`,
          "url": siteUrl,
          "name": siteName,
          "description": siteDescription,
          "publisher": {
            "@id": `${siteUrl}/#organization`
          },
          "blogPost": [],
          "about": [
            "Travel", "Asia", "Southeast Asia", "Travel Planning", 
            "Tourism", "Adventure", "Cultural Tourism", "Backpacking"
          ]
        })}
      </script>
      
      {/* Geo Tags */}
      <meta name="geo.region" content="ASIA" />
      <meta name="geo.placename" content="Asia, Southeast Asia, Middle East" />
      
      {/* Mobile Specific */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
    </Helmet>
  );
};

export default GlobalSEO;
